import React, { useCallback, useState } from 'react'
import { Link } from 'react-router'
import clsx from 'classnames'
import { APIService } from 'api/APIService'
import { cleanPhone } from 'utils/cleanPhone'
import { tryCatchAsync } from 'utils/tryCatch'
import { useObject } from 'src/hooks/useObject'
import { ShadowInput } from 'components/FormElements/ShadowInput/ShadowInput'
import { AppButton } from 'components/FormElements/AppButton/AppButton'
import { StatusModal } from 'components/Utility/StatusModal/StatusModal'
import { usePhoneField } from '../usePhoneField'
import formStyles from './SignInForm.module.scss'
import styles from '../Form.module.scss'

type SignInFormProps = {
  onSuccess: (token: string) => void,
}

/** Phone and password sign in (#signin) */
export const SignInForm: React.FC<SignInFormProps> = ({ onSuccess }) => {
  const [formData, setFormField] = useObject({ phone: '', password: '' })
  const [formError, setFormError] = useState<string | null>(null)
  const setPhone = useCallback((value: string) => setFormField('phone', value), [setFormField])
  const phoneField = usePhoneField(formData.phone, setPhone)

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    const phone = cleanPhone(formData.phone)
    if (!phone) {
      setFormError('Не указан номер телефона')
      return
    }
    if (!formData.password) {
      setFormError('Не указан пароль')
      return
    }
    await tryCatchAsync(async () => {
      onSuccess(await APIService.auth.signIn({ ...formData, phone }))
    }, (e) => setFormError(e.message))
  }, [formData, onSuccess])

  const dismissError = useCallback(() => setFormError(null), [])

  return (
    <form noValidate onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.fields}>
        <ShadowInput
          {...phoneField}
          className={styles.field}
          shadowColor={'yellow'}
          placeholder={'Телефон'}
        />
        <ShadowInput
          lang={'en'}
          onChange={(value) => setFormField('password', value)}
          value={formData.password}
          type={'password'}
          className={styles.field}
          shadowColor={'pink'}
          placeholder={'Пароль'}
        />
        <Link className={clsx([styles.link, formStyles.signUpLink])} to={'#signup'}>
          Регистрация
        </Link>
      </div>
      <div className={styles.actions}>
        <AppButton type={'submit'} className={styles.action}>
          Войти
        </AppButton>
        <Link className={clsx([styles.link, formStyles.recoverLink])} to={'#recover'}>
          Восстановить пароль
        </Link>
      </div>
      <StatusModal
        status={'error'}
        modalText={formError}
        buttonText={'ОК'}
        onClose={dismissError}
        onButtonClick={dismissError}
        isOpen={formError !== null}
      />
    </form>
  )
}
